import { InAppBrowser } from '@capacitor/inappbrowser';
import lscache from 'lscache';
import { forceTokenRemoval } from 'modules/authentication/helpers/auth';
import { requestOptionsWithSessionToken } from 'modules/authentication/helpers/requestHelper';
import { getCookieConsentSerialized } from 'providers/CookieConsentProvider';
import { isCordova, isIosApp } from 'utils/platform';
import { isAndroidApp } from 'utils/platform';
import useAuthStore from '~/authentication/store';
import { SearchAdsInformation } from '~/cordovaPlugins/Diagnostics';

import { WlWindow } from '../../typings/globals/cordova';

declare const window: WlWindow;

function addDefaultQueryParams(url: string) {
  const params = new URLSearchParams();

  if (isCordova()) {
    params.set('utm_source', 'app');
    params.set('utm_medium', ANALYTICS_PLATFORM);
    params.set('consent', getCookieConsentSerialized());

    if (isIosApp()) {
      const adInfo = lscache.get('apple_search_ads') as SearchAdsInformation;
      if (adInfo) {
        params.set('utm_campaign', String(adInfo?.campaignId));
        params.set('utm_medium', 'apple_search_ads');
        params.set('utm_term', String(adInfo?.keywordId));
        params.set('utm_content', String(adInfo?.adId));
      }
    }
  }

  const paramString = params.toString();

  if (url.indexOf('?') > -1) {
    return `${url}&${paramString}`;
  } else {
    return `${url}?${paramString}`;
  }
}

export async function openExternalLink(
  url: string,
  openInNewTab = true,
  tries = 0,
) {
  const urlWithDefaultParams = addDefaultQueryParams(url);
  if (!isCordova()) {
    const target = openInNewTab ? '_blank' : '_self';
    window.open(urlWithDefaultParams, target);
    return;
  }

  const tokenFromStorage = useAuthStore.getState().token;

  const requestOptions: RequestInit = requestOptionsWithSessionToken(
    {
      method: 'POST',
    },
    tokenFromStorage,
  );

  try {
    const shortLivedTokenResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/v1/auth/token/short/`,
      requestOptions,
    );
    const shortLivedTokenResponseJson = await shortLivedTokenResponse.json();

    const shortLivedToken = shortLivedTokenResponseJson.token;
    const searchParams = new URLSearchParams();

    if (shortLivedToken) {
      searchParams.set('token', shortLivedToken);
    }
    searchParams.set('redirect_uri', urlWithDefaultParams);

    await InAppBrowser.openInExternalBrowser({
      url: `${URL_AUTHENTICATION_HOST}/api/v1/auth/token/short/?${searchParams.toString()}`,
    });
  } catch (error) {
    const shouldCheckForTooLargeCookies = isAndroidApp() && window.cookieMaster;
    if (shouldCheckForTooLargeCookies) {
      console.error(
        'Failed to fetch short lived token, clearing existing auth token and cookies and retrying openExternalLink',
        error,
      );
      forceTokenRemoval(error, false);
      if (tries < 3) {
        await openExternalLink(url, openInNewTab, tries + 1);
      } else {
        console.error('Failed to openExternalLink after 3 tries', error);
      }
    }
  }
}

export async function openExternalLinkSimple(
  url: string,
  openInNewTab = false,
) {
  const urlWithDefaultParams = addDefaultQueryParams(url);
  if (isCordova()) {
    await InAppBrowser.openInExternalBrowser({
      url: urlWithDefaultParams,
    });
    return;
  }
  if (!isCordova() && openInNewTab) {
    window.open(urlWithDefaultParams, '_blank');
    return;
  }
  window.location.href = urlWithDefaultParams;
}
